import moment from "moment";
import { ReactNode } from "react";
import Button from "../../components/form-control/Button";
import List from "../../components/elements/List";
import { Transaction } from "../../components/models/transaction/Transaction";
import NoDataFound from "../../components/elements/NoDataFound";
import TransactionTypeBadge from "../../components/elements/TransactionTypeBadge";
import Card from "../../components/elements/Card";
import CediSign from "../../components/elements/CediSign";
import Avatar from "../../components/elements/Avatar";
import { getNameInitials } from "../../utilities/miscFunctions";
import StatusBadge from "../../components/elements/StatusBadge";
import { STATUS_OPTIONS } from "../../resources/enums";
import Loader from "../../components/Loader";

interface IProps {
    transactions: Transaction[];
    onAddTransaction?: () => void;
    loading?: boolean;
}

export default function RecentTransactions(props: IProps) {

    if (props.loading) {
        return <Loader message="Loading transactions..." />
    }

    const getListItemElement = (item: Record<string, any>, key: string): ReactNode => {
        const value = item[key as keyof Transaction];

        switch (key) {
            case ("createdAt"):
                return <span className="text-gray-600 dark:text-text-dark-primary">{moment(value as string).format("DD/MM/YYYY hh:mm A")}</span>

            case ("account"):
                return <div className="flex items-center gap-3">
                    <Avatar
                        src={value?.customer?.picture_url}
                        nameInitials={getNameInitials(`${value?.name}`)}
                        size={"sm"}
                    />

                    <div>
                        <p className="font-medium text-gray-900 dark:text-text-dark-primary">
                            {`${value?.name}`}
                        </p>
                        <p>
                            {value.type} | {value.accountNumber} | <CediSign /> {value.computedBalance}
                        </p>
                    </div>
                </div>
            case ("type"):
                return <TransactionTypeBadge type={value} is_system_transaction={item.is_system_transaction} />

            case ("amount"):
                return <span
                    className={`${(item as Transaction).isDeposit() ? "text-green-600" : "text-red-500"
                        }`}
                >
                    <CediSign /> {Math.abs(Number(value))}
                </span>

            case ("balance_after"):
                return <span className="text-gray-700 dark:text-text-dark-primary">
                    {value && <CediSign />}  {String(value ?? "-")}
                </span>

            case "status":
                return <span><StatusBadge status={value as STATUS_OPTIONS} /></span>;

            case "transaction_mode":
                return <span>{value}</span>;

            default:
                return <span className="text-gray-600 dark:text-text-dark-primary">{value as string}</span>
        }
    }

    return (
        <Card header={<h2 className="text-lg font-semibold mb-2 p-4">
            Recent Transactions
        </h2>} >

            {props.transactions.length > 0 ? (
                <List
                    list={props.transactions}
                    columns={["Date", "Account", "Type", "Amount", "Mode", "Status", "Balance"]}
                    accessors={["createdAt", "account", "type", "amount", "transaction_mode", "status", "balance_after"]}
                    getListItemElement={getListItemElement}
                />
            ) : (
                <div className="flex justify-center flex-col items-center">
                    <NoDataFound text="No transactions found" />
                    {props.onAddTransaction && (
                        <div className="w-[200px] mt-6">
                            <Button
                                label="Add Transaction"
                                className="bg-primary-secondary rounded text-white"
                                onClick={() => props.onAddTransaction?.()}
                            />
                        </div>
                    )}
                </div>
            )}
        </Card>

    );
}